import { animated, useSpring } from "@react-spring/three";
import { useGLTF } from "@react-three/drei";
import React, { useContext, useEffect, useMemo } from "react";
import { DoubleSide, TextureLoader } from "three";
import {
  BoltClosePos,
  BoltOpenPosBack,
  BoltOpenPosBottom,
  BoltOpenPosFront,
  BoltOpenPosTop,
  Deg0,
  DoorDeg90CCW,
  HandleDeg360CCW,
  Scale0,
  Scale1,
} from "../../utils/AnimaionSprings";
import CurrentContext from "../../utils/CurrentContext";
import useChangedTexture from "../../utils/customHooks/useChangedTexture";
import useTextureCustom from "../../utils/customHooks/useTextureCustom";
import {
  BlackPlastic,
  BoltMaterial,
  PaintMaterial,
  PocketMaterial,
} from "../../utils/finsishesData";
import { handleExportGLTF } from "../../utils/jsHelper";
import { LockMechanicalLarge, LockSecuramBacklit, LockSecuramProLogic } from "./model_locks";
import useIsMobile from "../../utils/customHooks/useIsMobile";

//region Default options
const defaultoptions = [
  {
    materialName: "body_paint",
    finishToApply: "burgundymarble",
  },
  {
    materialName: "interior_fabric",
    finishToApply: "beigevelour",
  },
  {
    materialName: "securam_prologic",
    finishToApply: "Lock_securam_prologic",
  },
  {
    materialName: "mechanical_lock",
    finishToApply: "Lock_mechanical_large",
  },
  {
    materialName: "interior_fabric_shelf",
    finishToApply: "woodgrainvinyl",
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item.materialName], item.finishToApply);
};

export function NationalMagnum40({ animateInit, currentVariant, lockType }) {

  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile ? "models/nationalmagnum40-m.glb" : "models/nationalmagnum40.glb");
  const mobileModel = useGLTF("models/nationalmagnum40-m.glb");

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          animateInit,
          currentVariant,
          lockType,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            animateInit,
            currentVariant,
            lockType,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            animateInit,
            currentVariant,
            lockType,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  animateInit,
  currentVariant,
  lockType,
  visible = true,
  mobileProp = false,
}) => {
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
  } = useContext(CurrentContext);

  // console.log("nodes", nodes);
  //region animation springs
  const { rotation_Door } = useSpring({
    rotation_Door: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg360CCW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg360CCW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Back } = useSpring({
    position_Bolt_Back: doorOpen ? BoltOpenPosBack : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Top } = useSpring({
    position_Bolt_Top: doorOpen ? BoltOpenPosTop : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { position_Bolt_Bottom } = useSpring({
    position_Bolt_Bottom: doorOpen ? BoltOpenPosBottom : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: "nationalmagnum40",
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR,mobileProp]);

  const loader = useMemo(() => new TextureLoader(), []);

  const decalDoorLogo = useMemo(
    () => loader.load("textures/decal_door_nationalmagnum40.png"),
    []
  );
  defaultoptions.map((item) => {
    HandleUpdateTexture(item, materials);
  });

  const materialNames = {
    interior_materials: "interior_fabric",
    outer_materials: "body_paint",
    hardware_materials: ["wheel_handle", "mechanical_lock", "securam_prologic"],
  };

  useChangedTexture(
    currentVariant,
    materials,
    materialNames,
    HandleUpdateTexture
  );
  useTextureCustom(PaintMaterial, currentVariant.outer);

  const woodenshelf =
    currentVariant.woodshelf === "woodgrainvinyl" ? true : false;

  const decalOuterColor =
    currentVariant.hardware === "blackchromehardware"
      ? "#515151"
      : currentVariant.hardware === "chromehardware"
      ? "#e5e5e5"
      : currentVariant.hardware === "brasshardware"
      ? "#C1A163"
      : "#515151";

  const poketOutline =
    currentVariant.interior === "beigevelour"
      ? "#9B784B"
      : currentVariant.interior === "silvervelour"
      ? "#64615D"
      : "#9B784B";

  PocketMaterial.color.set(poketOutline);
  materials["bag_decal 3"].color.set(poketOutline);

  const feathered_edge =
    currentVariant.outer === "graycharcoalfeatherededge"
      ? true
      : currentVariant.outer === "champagnefeatherededge"
      ? true
      : currentVariant.outer === "emeraldgreenfeatherededge"
      ? true
      : currentVariant.outer === "burgundymetallicfeatherededge"
      ? true
      : false;

  return (
    <group dispose={null} visible={visible}>
      <animated.group
        position={[0.372, 0.081, 0.35741]}
        rotation={rotation_Door}
      >
        <animated.mesh
          position={position_Bolt_Back}
          castShadow
          geometry={nodes.bolts_back.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Front}
          castShadow
          geometry={nodes.bolts_front.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Bottom}
          castShadow
          geometry={nodes.bolts_bottom.geometry}
          material={BoltMaterial}
        />
        <animated.mesh
          position={position_Bolt_Top}
          castShadow
          geometry={nodes.bolts_top.geometry}
          material={BoltMaterial}
        />
        <mesh
          castShadow
          geometry={nodes.bolt_housing.geometry}
          // material={BoltMaterial}
          material={BlackPlastic}
        />
        {/* Locks */}
        <group position={[-0.371, 1.092, -0.015]}>
          <LockMechanicalLarge
            material={materials.mechanical_lock}
            // visible={lockType === "mechanicallock" ? true : false}
            scale={lockType === "mechanicallock" ? [1, 1, 1] : [0, 0, 0]}
            decalOuterColor={decalOuterColor}
          />
          {/* <LockSecuramProLogic
            material={materials.securam_prologic}
            // visible={lockType === "securamprologic" ? true : false}
            scale={lockType === "securamprologic" ? [1, 1, 1] : [0, 0, 0]}
          /> */}
          <LockSecuramBacklit
            material={materials.wheel_handle}
            scale={lockType === "securambacklit" ? [1, 1, 1] : [0, 0, 0]}
          />
        </group>
        {/* Door Exterior */}
        <group>
          <mesh
            castShadow
            geometry={nodes.outer_door.geometry}
            material={PaintMaterial}
          ></mesh>
          <mesh position={[-0.37, 0.751, 0]} scale={[2.2, 2.2, 1]}>
            <planeGeometry args={[0.7, 0.7]} />
            <meshBasicMaterial
              color={decalOuterColor}
              map={decalDoorLogo}
              transparent={true}
              //side={DoubleSide}
            />
          </mesh>
        </group>

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_1.geometry}
          material={materials.Pockets_Door}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_1.geometry}
          material={materials.Pockets_Door}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_2.geometry}
          material={PocketMaterial}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_3.geometry}
          material={materials.Door_Zippers}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_4.geometry}
          material={materials.Door_Base_Decal}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_5.geometry}
          material={PocketMaterial}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_6.geometry}
          material={materials.Door_Pockets_Elastic}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_7.geometry}
          material={materials["bag_decal 3"]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_8.geometry}
          material={materials.Door_Pocket_Nets}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.pockets_base_9.geometry}
          material={materials.Door_Bag_Strap}
        />

        <animated.group
          position={[-0.503, 0.817, 0.042]}
          rotation={handle_Rotation1}
          scale={handle_Scale1}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_1.geometry}
            material={materials.wheel_handle}
          />
          <mesh
            position={[0, 0, 0.005]}
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_2.geometry}
            material={materials.Handle_Plane}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_3.geometry}
            material={materials.Wheel_Decal}
          />
        </animated.group>
        <animated.group
          position={[-0.503, 0.817, 0.042]}
          rotation={handle_Rotation2}
          scale={handle_Scale2}
        >
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_1.geometry}
            material={materials.wheel_handle}
          />
          <mesh
            position={[0, 0, 0.005]}
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_2.geometry}
            material={materials.Handle_Plane}
          />
          <mesh
            castShadow
            receiveShadow
            geometry={nodes.wheel_handle_3.geometry}
            material={materials.Wheel_Decal}
          />
        </animated.group>
      </animated.group>
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.door_hinges.geometry}
        // material={materials.door_hinges}
        material={materials.wheel_handle}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior.geometry}
        material={materials.interior_fabric}
      />
      <mesh
        castShadow
        geometry={nodes.outer_body.geometry}
        material={PaintMaterial}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.shelf.geometry}
        material={
          woodenshelf
            ? materials.interior_fabric_shelf
            : materials.interior_fabric
        }
        // position={[0.001, 1.005, 0.324]}
      />

      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_1.geometry}
        material={materials.Bars}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_2.geometry}
        material={materials.Safe_Interior_Hinges}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_3.geometry}
        material={materials.Cable}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_4.geometry}
        material={materials.Cable_chrome}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_5.geometry}
        material={materials.Shelf_Border_Horizontal}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_6.geometry}
        material={materials.Shelf_Border_Plastic}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_7.geometry}
        material={BlackPlastic}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.feathered_edge.geometry}
        material={materials.feathered_edge}
        visible={feathered_edge}
      />
    </group>
  );
}

//useGLTF.preload("models/nationalmagnum40.glb");
